/*
 * File: AllowedTextFormat.ts
 * Project: mp-mobile-app
 *
 * Created by Cole on 02/16/2022
 *
 * Copyright (c) 2022 MacPractice, Inc. All rights reserved.
 *
 * Description: Definition for localization preference values
 */

import { flow, SnapshotIn, types } from "mobx-state-tree";
import { PreferenceBase } from "./PreferenceBase";

export const CharsetFormat = types.model("CharsetFormat", {
  kind: types.literal("charset"),
  charset: types.enumeration(["alphabet", "all", "capitalizedAlphabet"]),
  limit: types.maybe(types.number)
});

export const UsPhoneFormat = types.model("UsPhoneFormat", {
  kind: types.literal("usPhone")
});

export const UsZipCodeFormat = types.model("UsZipCodeFormat", {
  kind: types.literal("usZipCode")
});

export const FreeformFormat = types.model("FreeformFormat", {
  kind: types.literal("freeform")
});

export const DateFormat = types.model("DateFormat", {
  kind: types.literal("date"),
  format: types.model("_DateFormatOptions", {
    order: types.enumeration(["dayMonthYear", "monthDayYear"]),
    separator: types.string
  })
});

export const AllowedTextFormat = types.union(
  CharsetFormat,
  UsPhoneFormat,
  UsZipCodeFormat,
  FreeformFormat,
  DateFormat
);

export const AllowedTextFormatPreference = types
  .compose(
    "AllowedTextFormatPreference",
    PreferenceBase,
    types.model("_AllowedTextFormatPreference", {
      value: types.maybe(AllowedTextFormat)
    })
  )
  .actions(self => ({
    /**
     * Returns the value that should be sent to the server representing this preferences update. This method does not update the preference with the new value.
     * @param key The database pkey this preference represents.
     * @param value The new value of the preference.
     * @returns The value representing this update to be sent to the server.
     */
    valueForUpdate(key: string, value: SnapshotIn<typeof AllowedTextFormat>) {
      return value;
    },
    /**
     * Applies a loaded preference value from the database to this preference object. If the value needs to have some work done to it before storing it (e.g. fetching models from IDs) that happens in this method.
     * @param value The new value from the server.
     */
    // disable lint to keep the interface the same
    // eslint-disable-next-line require-yield
    applyLoadedPrefValue: flow(function*(value) {
      self.value = value;
    })
  }));
